/**
 * Escape closes the TOPMOST overlay only.
 *
 * The BAB Report stacks three overlays (account modal → booking modal → comment panel).
 * Plain per-component keydown listeners all fire on the same target, so one Escape would
 * collapse the whole stack. A module-level stack fixes that: the last overlay to mount is
 * the only one that reacts.
 */
import { useEffect, useRef } from 'react';

type Entry = { current: () => void };

const stack: Entry[] = [];

function onKeyDown(e: KeyboardEvent) {
    if (e.key !== 'Escape') return;
    const top = stack[stack.length - 1];
    if (!top) return;
    e.stopPropagation();
    top.current();
}

export function useTopmostEscape(active: boolean, onEscape: () => void) {
    // The stack position must reflect when the overlay OPENED, not when its parent last
    // re-rendered — so the entry is a ref and only `active` drives the effect.
    const handler = useRef(onEscape);
    handler.current = onEscape;

    useEffect(() => {
        if (!active) return;

        if (stack.length === 0) window.addEventListener('keydown', onKeyDown, true);
        stack.push(handler);

        return () => {
            const index = stack.lastIndexOf(handler);
            if (index !== -1) stack.splice(index, 1);
            if (stack.length === 0) window.removeEventListener('keydown', onKeyDown, true);
        };
    }, [active]);
}
